<script setup lang="ts">
import type { CropRect } from './screen-capture';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';

/**
 * 截图框选：客服窗口此时由宿主铺满整页，这里把截到的整页画面铺满显示，客户拖动框选要发送的区域。
 * 拖框内部可移动选区；回车或双击完成，Esc 取消；不框选直接完成就发送整页。
 */
const props = defineProps<{ source: HTMLCanvasElement }>();
const emit = defineEmits<{ cancel: []; done: [rect: CropRect | null] }>();

interface Box { h: number; w: number; x: number; y: number }

const root = ref<HTMLElement | null>(null);
const imageUrl = ref('');
const viewport = ref({ height: 0, width: 0 });
const selection = ref<Box | null>(null);
let drag: null | { mode: 'draw' | 'move'; originX: number; originY: number; start: Box | null } = null;

/** 图片按比例完整显示在窗口里的位置和大小。 */
const imageBox = computed<Box>(() => {
  const { height, width } = viewport.value;
  const scale = Math.min(width / props.source.width, height / props.source.height) || 1;
  const w = props.source.width * scale;
  const h = props.source.height * scale;
  return { h, w, x: (width - w) / 2, y: (height - h) / 2 };
});

const toolbarStyle = computed(() => {
  const area = selection.value ?? imageBox.value;
  const below = area.y + area.h + 44 <= viewport.value.height;
  const top = below ? area.y + area.h + 8 : Math.max(8, area.y + area.h - 44);
  const right = Math.max(8, viewport.value.width - (area.x + area.w));
  return { right: `${right}px`, top: `${top}px` };
});

function measure(): void {
  const rect = root.value?.getBoundingClientRect();
  if (rect) viewport.value = { height: rect.height, width: rect.width };
}

function clampPoint(clientX: number, clientY: number): { x: number; y: number } {
  const box = imageBox.value;
  return {
    x: Math.min(Math.max(clientX, box.x), box.x + box.w),
    y: Math.min(Math.max(clientY, box.y), box.y + box.h),
  };
}

function inside(box: Box, x: number, y: number): boolean {
  return x >= box.x && x <= box.x + box.w && y >= box.y && y <= box.y + box.h;
}

function onPointerDown(event: PointerEvent): void {
  if (event.button !== 0) return;
  const point = clampPoint(event.clientX, event.clientY);
  const current = selection.value;
  drag = current && inside(current, event.clientX, event.clientY)
    ? { mode: 'move', originX: event.clientX, originY: event.clientY, start: { ...current } }
    : { mode: 'draw', originX: point.x, originY: point.y, start: null };
  if (drag.mode === 'draw') selection.value = { h: 0, w: 0, x: point.x, y: point.y };
  (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
}

function onPointerMove(event: PointerEvent): void {
  if (!drag) return;
  const box = imageBox.value;
  if (drag.mode === 'move' && drag.start) {
    const x = Math.min(Math.max(drag.start.x + event.clientX - drag.originX, box.x), box.x + box.w - drag.start.w);
    const y = Math.min(Math.max(drag.start.y + event.clientY - drag.originY, box.y), box.y + box.h - drag.start.h);
    selection.value = { ...drag.start, x, y };
    return;
  }
  const point = clampPoint(event.clientX, event.clientY);
  selection.value = {
    h: Math.abs(point.y - drag.originY),
    w: Math.abs(point.x - drag.originX),
    x: Math.min(point.x, drag.originX),
    y: Math.min(point.y, drag.originY),
  };
}

function onPointerUp(): void {
  if (drag?.mode === 'draw' && selection.value && (selection.value.w < 6 || selection.value.h < 6)) selection.value = null;
  drag = null;
}

/** 选区换算成原图像素。 */
function sourceRect(): CropRect | null {
  const current = selection.value;
  if (!current) return null;
  const box = imageBox.value;
  const scale = props.source.width / box.w;
  return { height: current.h * scale, width: current.w * scale, x: (current.x - box.x) * scale, y: (current.y - box.y) * scale };
}

function finish(): void {
  emit('done', sourceRect());
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault();
    emit('cancel');
  } else if (event.key === 'Enter') {
    event.preventDefault();
    finish();
  }
}

onMounted(async () => {
  measure();
  window.addEventListener('resize', measure);
  window.addEventListener('keydown', onKeydown);
  props.source.toBlob((blob) => {
    if (blob) imageUrl.value = URL.createObjectURL(blob);
  }, 'image/png');
  await nextTick();
  root.value?.focus();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', measure);
  window.removeEventListener('keydown', onKeydown);
  if (imageUrl.value) URL.revokeObjectURL(imageUrl.value);
});
</script>

<template>
  <div
    ref="root"
    class="capture-crop"
    data-testid="capture-crop"
    role="dialog"
    aria-label="框选截图区域"
    tabindex="-1"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
    @dblclick="selection && finish()"
  >
    <img
      v-if="imageUrl"
      class="capture-crop__image"
      :src="imageUrl"
      alt=""
      draggable="false"
      :style="{ left: `${imageBox.x}px`, top: `${imageBox.y}px`, width: `${imageBox.w}px`, height: `${imageBox.h}px` }"
    />
    <div v-if="!selection" class="capture-crop__dim" :style="{ left: `${imageBox.x}px`, top: `${imageBox.y}px`, width: `${imageBox.w}px`, height: `${imageBox.h}px` }"></div>
    <div
      v-else
      class="capture-crop__selection"
      data-testid="capture-selection"
      :style="{ left: `${selection.x}px`, top: `${selection.y}px`, width: `${selection.w}px`, height: `${selection.h}px` }"
    ></div>
    <p v-if="!selection" class="capture-crop__hint">拖动鼠标框选要发送的区域，按 Esc 取消</p>
    <div class="capture-crop__toolbar" :style="toolbarStyle" @pointerdown.stop>
      <button type="button" data-testid="capture-cancel" @click="emit('cancel')">取消</button>
      <button type="button" class="capture-crop__done" data-testid="capture-done" @click="finish">
        {{ selection ? '完成' : '截取整页' }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.capture-crop {
  position: fixed;
  inset: 0;
  z-index: 1000;
  overflow: hidden;
  background: #1f2329;
  cursor: crosshair;
  outline: none;
  touch-action: none;
  user-select: none;
}
.capture-crop__image { position: absolute; pointer-events: none; }
.capture-crop__dim { position: absolute; background: rgb(0 0 0 / 30%); pointer-events: none; }
.capture-crop__selection {
  position: absolute;
  border: 1px solid var(--w-primary, #17a2b8);
  box-shadow: 0 0 0 9999px rgb(0 0 0 / 45%);
  cursor: move;
}
.capture-crop__hint {
  position: absolute;
  top: 16px;
  left: 50%;
  margin: 0;
  padding: 6px 14px;
  border-radius: 16px;
  background: rgb(0 0 0 / 65%);
  color: #fff;
  font-size: 13px;
  pointer-events: none;
  transform: translateX(-50%);
}
.capture-crop__toolbar {
  position: absolute;
  display: flex;
  gap: 6px;
  padding: 4px;
  border-radius: 6px;
  background: #fff;
  box-shadow: 0 4px 16px rgb(0 0 0 / 25%);
  cursor: default;
}
.capture-crop__toolbar button {
  height: 28px;
  padding: 0 12px;
  border: 0;
  border-radius: 4px;
  background: #f2f3f5;
  color: #1f2329;
  font-size: 13px;
  cursor: pointer;
}
.capture-crop__toolbar .capture-crop__done { background: var(--w-primary, #17a2b8); color: #fff; }
</style>
